import { useState } from "react";
import type { Unit } from "../../types/company";
import { PLANS, TODAY } from "../../data/company";
import {
  addBlock,
  addDaysIso,
  changePlan,
  currentPlan,
  activeListingCount,
  findListing,
  isUrgent,
  listingName,
  planName,
} from "../../lib/company";
import { startSponsorCheckout } from "../../lib/companyApi";
import { errorText } from "../../lib/api";
import { useCompany } from "../../lib/companyContext";
import { eur, longDate } from "../../lib/format";
import { ChoiceRow, Modal } from "./ui";
import { useT } from "../../i18n";

/** Real today ("YYYY-MM-DD"): sponsorships run on real dates, not the demo TODAY. */
const todayIso = () => new Date().toISOString().slice(0, 10);

const smallInput =
  "h-10.5 w-full rounded border-0 bg-surface px-3 text-sm outline-none focus:ring-1 focus:ring-brand";

/** Shown when publishing would exceed the plan's listing limit. */
export function UpgradeModal({ onClose, onUpgraded }: { onClose: () => void; onUpgraded: () => void }) {
  const t = useT();
  const m = t.company.modals;
  const { state, run, toast } = useCompany();
  const plan = currentPlan(state);
  const next = PLANS.find((p) => p.listingLimit > (plan?.listingLimit ?? 0));

  return (
    <Modal
      title={m.upgradeTitle}
      text={m.upgradeText(plan ? planName(plan) : "", plan?.listingLimit ?? 0, activeListingCount(state))}
      okLabel={next ? m.upgradeOk(planName(next), eur(next.price)) : undefined}
      cancelLabel={m.later}
      onClose={onClose}
      onOk={() => {
        if (!next) return;
        run(changePlan, next.id);
        toast(t.company.subscription.planActivated(planName(next)));
        onClose();
        onUpgraded();
      }}
    />
  );
}

/** French VAT added at checkout to the prices set excl. VAT. */
const VAT_RATE = 0.2;

/** Sponsored listing: badge and top of results, paid on Stripe Checkout. */
export function SponsorModal({ listingId, onClose }: { listingId: string; onClose: () => void }) {
  const t = useT();
  const m = t.company.sponsor;
  const { state } = useCompany();
  const listing = findListing(state, listingId)!;
  const options = state.pricing?.sponsor.enabled ? state.pricing.sponsor.options : [];
  const active = isUrgent(listing);
  const from = active ? listing.urgentUntil! : todayIso();
  const [days, setDays] = useState(options[0]?.days ?? 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  /** Opens Stripe Checkout; the sponsorship starts when we come back paid. */
  const sponsor = async () => {
    setBusy(true);
    setError("");
    try {
      window.location.assign(await startSponsorCheckout(listingId, days));
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };
  const selected = options.find((o) => o.days === days);

  return (
    <Modal
      title={active ? m.extendTitle : m.makeTitle}
      text={m.text}
      okLabel={options.length ? (busy ? m.redirecting : selected ? m.pay(eur(selected.price * (1 + VAT_RATE))) : m.ok) : undefined}
      okVariant="primary"
      error={error}
      onClose={onClose}
      onOk={() => {
        if (!busy && days) void sponsor();
      }}
    >
      <div className="mb-3 flex flex-wrap justify-between gap-2.5 rounded-md border border-line-strong px-3.5 py-3">
        <div>
          <div className="text-[15px] font-medium">{listingName(listing)}</div>
          <div className="text-[13px] text-muted">
            {listing.city} · {eur(listing.weekly)}
            {t.common.perWeek}
          </div>
        </div>
        {active && <span className="self-center text-xs text-brand-link">{m.activeUntil(longDate(listing.urgentUntil!))}</span>}
      </div>
      {options.length ? (
        <>
          <div className="mb-3 grid gap-1.5">
            {options.map((o) => (
              <ChoiceRow key={o.days} name="sponsor" checked={days === o.days} onChange={() => setDays(o.days)}>
                <span className="flex-1 text-sm">{m.option(o.days, eur(o.price))}</span>
                <span className="text-xs text-muted">{m.untilDate(longDate(addDaysIso(from, o.days)))}</span>
              </ChoiceRow>
            ))}
          </div>
          <p className="m-0 text-xs text-faint">{m.note}</p>
          {selected && (
            <p className="m-0 mt-1 text-xs text-faint">
              {m.vatLine(eur(selected.price), eur(selected.price * VAT_RATE), eur(selected.price * (1 + VAT_RATE)))}
            </p>
          )}
        </>
      ) : (
        <p className="m-0 text-sm text-muted">{m.unavailable}</p>
      )}
    </Modal>
  );
}

/** Makes a physical vehicle unavailable for a period. */
export function BlockDatesModal({ unit, onClose }: { unit: Unit; onClose: () => void }) {
  const t = useT();
  const m = t.company.modals;
  const { run, toast } = useCompany();
  const [start, setStart] = useState(TODAY);
  const [end, setEnd] = useState(addDaysIso(TODAY, 3));
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  return (
    <Modal
      title={m.blockTitle(unit.code)}
      text={m.blockText}
      okLabel={m.blockOk}
      okVariant="dark"
      error={error}
      onClose={onClose}
      onOk={() => {
        const result = run(addBlock, unit.id, start, end, reason);
        if (!result.ok) return setError(result.message);
        toast(m.blocked);
        onClose();
      }}
    >
      <div className="mb-2 grid grid-cols-2 gap-2">
        <input type="date" aria-label={m.from} value={start} onChange={(e) => setStart(e.target.value)} className={smallInput} />
        <input type="date" aria-label={m.to} value={end} min={start} onChange={(e) => setEnd(e.target.value)} className={smallInput} />
      </div>
      <input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={m.reasonPlaceholder}
        className={smallInput}
      />
    </Modal>
  );
}
